import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { driver, Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useOnboardingStore, TourId } from '../store/onboardingStore';
import {
  welcomeTourSteps,
  WELCOME_TOUR_SETTINGS_NAV_INDEX,
  WELCOME_TOUR_DASHBOARD_NAV_INDEX,
  dashboardTourSteps,
  pipelineNewTourSteps,
  pipelineQueueTourSteps,
  pipelineApprovedTourSteps,
  pipelineUploadedTourSteps,
  pipelineReturnedTourSteps,
  pipelineIssuesTourSteps,
  caseReviewTourSteps,
  settingsTourSteps,
  sidebarTourSteps,
} from '../components/onboarding/tours';

const tourStepsMap: Record<TourId, typeof welcomeTourSteps> = {
  'welcome': welcomeTourSteps,
  'dashboard': dashboardTourSteps,
  'pipeline-new': pipelineNewTourSteps,
  'pipeline-queue': pipelineQueueTourSteps,
  'pipeline-approved': pipelineApprovedTourSteps,
  'pipeline-uploaded': pipelineUploadedTourSteps,
  'pipeline-returned': pipelineReturnedTourSteps,
  'pipeline-issues': pipelineIssuesTourSteps,
  'case-review': caseReviewTourSteps,
  'settings': settingsTourSteps,
  'sidebar': sidebarTourSteps,
};

export function useOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTour, completeTour, endTour } = useOnboardingStore();
  const driverRef = useRef<Driver | null>(null);
  const pendingStepRef = useRef<number | null>(null);

  // Handle navigation completion and resume tour
  useEffect(() => {
    if (pendingStepRef.current === null || !driverRef.current) {
      return;
    }

    const stepIndex = pendingStepRef.current;
    pendingStepRef.current = null;

    // Small delay to let DOM settle after navigation
    const timer = setTimeout(() => {
      if (driverRef.current) {
        driverRef.current.drive(stepIndex);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  const runTour = useCallback((tourId: TourId) => {
    const steps = tourStepsMap[tourId];
    if (!steps) return;

    const driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps,
      animate: true,
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.75)',
      stagePadding: 8,
      stageRadius: 8,
      popoverOffset: 12,
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Finish',
      onDestroyed: () => {
        driverRef.current = null;
        completeTour(tourId);
      },
      onCloseClick: () => {
        endTour();
        driverObj.destroy();
      },
      onNextClick: (_element, _step, opts) => {
        const currentIndex = opts.state.activeIndex ?? 0;

        // Handle welcome tour navigation
        if (tourId === 'welcome') {
          // Navigate to settings page
          if (currentIndex === WELCOME_TOUR_SETTINGS_NAV_INDEX - 1) {
            pendingStepRef.current = WELCOME_TOUR_SETTINGS_NAV_INDEX;
            navigate('/settings');
            return;
          }

          // Navigate back to dashboard
          if (currentIndex === WELCOME_TOUR_DASHBOARD_NAV_INDEX - 1) {
            pendingStepRef.current = WELCOME_TOUR_DASHBOARD_NAV_INDEX;
            navigate('/');
            return;
          }
        }

        // Default: move to next step
        driverObj.moveNext();
      },
      onPrevClick: (_element, _step, opts) => {
        const currentIndex = opts.state.activeIndex ?? 0;

        // Handle welcome tour navigation backwards
        if (tourId === 'welcome') {
          // Going back from settings intro to settings link on dashboard
          if (currentIndex === WELCOME_TOUR_SETTINGS_NAV_INDEX) {
            pendingStepRef.current = WELCOME_TOUR_SETTINGS_NAV_INDEX - 1;
            navigate('/');
            return;
          }

          // Going back from help trigger to user prompt on settings
          if (currentIndex === WELCOME_TOUR_DASHBOARD_NAV_INDEX) {
            pendingStepRef.current = WELCOME_TOUR_DASHBOARD_NAV_INDEX - 1;
            navigate('/settings');
            return;
          }
        }

        // Default: move to previous step
        driverObj.movePrevious();
      },
    });

    driverRef.current = driverObj;
    driverObj.drive();
  }, [completeTour, endTour, navigate]);

  // Run tour when activeTour changes
  useEffect(() => {
    if (!activeTour) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      runTour(activeTour);
    }, 100);
    return () => clearTimeout(timer);
  }, [activeTour, runTour]);

  return { runTour };
}
